import React, { useEffect, useState } from "react";
import { useLanguage } from "../LangTranslation/LanguageContext";
import FormikSelect from "../Formik/FormikSelect";
import FormikInput from "../Formik/FormikInput";
import { useDispatch } from "react-redux";
import { updateProductQuantity } from "../../Redux/Reducers/ProductSlice";
import {
  PRODUCT_INVENTORY_DEFAULT_LIST,
  TABLE_CELL_INPUT_MINIMUM_WIDTH,
} from "../../utilities/constants";
import SVGButton from "../Buttons/SVGButton";
import deleteIcon from "../../Images/Delete.svg";
import editIcon from "../../Images/edit.svg";
import doneIcon from "../../Images/check.svg";
import styled from "styled-components";
import { useFormikContext } from "formik";
import { searchReadGeneral } from "../../DataApi/generalApis/searchReadGeneral";
import { useSession } from "../../authentications/SessionContext";
import TableLoader from "../Loaders/TableLoader";
import { createGeneral } from "../../DataApi/generalApis/createGeneral";
import useListOptions from "../../CustomHooks/useListOptions";
import { primaryColor } from "../../utilities/colors";

const Button = styled.button`
  background-color: #fff;
  color: #ff5837;
  border: none;
  font-weight: 600;
  width: 130px;
  min-width: 150px;
  height: 40px;
  margin-right: 110px;
  outline: none; /* Remove default button outline */
`;

function ProductInventory({ selectedRecord }) {
  const { values, setFieldValue } = useFormikContext();
  const { inventory = [] } = values;
  const { language, translations } = useLanguage();
  const t = translations[language];

  const [productList, setProductList] = useState([]);
  const dispatch = useDispatch();

  const { sessionInfo } = useSession();
  const { company_id, context, isVTEX } = sessionInfo;
  const [isLoading, setIsLoading] = useState(true);

  const { options: warehouseList, handleCustomSearch } = useListOptions({
    model: "stock.location",
    name: "",
    args: [
      ["usage", "in", ["internal", "transit"]],
      ["company_id", "=", company_id],
    ],
  });

  const handleReadWriteData = async (input, index) => {
    const { product_id, inventory_quantity, location_id } = input;
    if (!product_id) return;
    try {
      const response = await createGeneral({
        context: {
          active_model: "product.template",
          active_id: selectedRecord.id,
          active_ids: [selectedRecord.id],
          hide_location: false,
          hide_lot: true,
          no_at_date: true,
          search_default_published: 1,
          search_default_on_hand: true,
          inventory_mode: true,
          product_tmpl_ids: [selectedRecord.id],
        },
        company_id,
        model: "stock.quant",
        payload: { product_id, inventory_quantity, location_id },
      });

      if (response?.result) {
        fetchInventoryDetail();

        // Calculate total inventory quantity
        const updatedInventory = inventory.map((item, i) =>
          i === index ? { ...item, inventory_quantity } : item
        );
        dispatch(
          updateProductQuantity({
            productId: selectedRecord.id,
            inventoryItems: updatedInventory,
          })
        );
      }
    } catch (error) {
      console.error("Error in handleReadWriteData:", error);
    }
  };

  const fetchInventoryDetail = () => {
    if (values.id) {
      setIsLoading(true);
      searchReadGeneral({
        context,
        company_id,
        fields: PRODUCT_INVENTORY_DEFAULT_LIST,
        model: "stock.quant",
        domain: [
          "&",
          ["product_id", "in", values.product_variant_ids],
          ["on_hand", "=", true], // fetch onhand quantity
        ],
      }).then(async (inventoryResponse) => {
        const updatedInventory = inventoryResponse?.result?.records?.map(
          (item) => ({
            ...item,
            location_id: item?.location_id?.[0],
            product_id: item?.product_id?.[0],
            inventory_quantity: item.quantity, // hand quantity => invntry_qty
            availabe_quantity: item.quantity,
            isDisabled: true,
            isEditable: false,
          })
        );

        const totalQuantityAvailable = updatedInventory.reduce(
          (total, item) => total + item.inventory_quantity,
          0
        );
        setFieldValue("inventory", updatedInventory);
        setFieldValue("qty_available", totalQuantityAvailable);
        setIsLoading(false);
        if (values.product_variant_ids?.length > 0) {
          handleCustomSearch({
            args: [
              "&",
              ["type", "=", "product"],
              ["product_tmpl_id", "in", [selectedRecord.id, 0]],
            ],
            model: "product.product",
            pagination: { limit: 30 },
            name: "",
            setState: setProductList,
          });
        }
      });
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchInventoryDetail();
  }, []);

  const checkDisableInputs = ({ isDisabled, isEditable }) => {
    if (isDisabled) {
      return true;
    }
    if (!isEditable) {
      return true;
    }
    return false;
  };

  const handleToggleInventoryRow = (item, index) => {
    const updatedInventory = [...inventory];

    // Toggle the `isEditable` field for the specific item at the given index
    updatedInventory[index] = {
      ...updatedInventory[index],
      isEditable: !item.isEditable,
      isDisabled: !item.isDisabled,
    };

    // Update the whole inventory array in Formik's state
    setFieldValue("inventory", updatedInventory);

    // Check the updated values
    // console.log("Updated inventory values:", updatedInventory);
  };

  const handleAddInventory = () => {
    const newInventoryItem = {
      location_id: "",
      product_id: values.product_variant_ids?.[0] || "",
      inventory_quantity: "",
      availabe_quantity: "",
      isDisabled: false,
      isEditable: true,
    };
    setFieldValue("inventory", [...inventory, newInventoryItem]);
  };

  return (
    <div className=" pb-4">
      <div className="row overflow-auto">
        <table className="table position-relative">
          <thead>
            <tr className="heading_row">
              {values?.product_variant_ids?.length > 1 && (
                <th style={{ minWidth: TABLE_CELL_INPUT_MINIMUM_WIDTH }}>
                  {t["Product"]}
                </th>
              )}
              <th style={{ minWidth: TABLE_CELL_INPUT_MINIMUM_WIDTH }}>
                {t["Location"]}
              </th>
              <th style={{ minWidth: TABLE_CELL_INPUT_MINIMUM_WIDTH }}>
                {t["On Hand Quantity"]}
              </th>
              <th style={{ minWidth: 20 }} />
            </tr>
          </thead>

          <tbody style={{ minHeight: 200 }}>
            {isLoading && (
              <tr>
                <td>
                  <TableLoader isLoading={isLoading} />
                </td>
              </tr>
            )}
            {inventory?.map((item, index) => (
              <tr key={index}>
                {values?.product_variant_ids?.length > 1 && (
                  <td>
                    <FormikSelect
                      name={`inventory.${index}.product_id`}
                      options={productList}
                    />
                  </td>
                )}
                <td>
                  <FormikSelect
                    name={`inventory.${index}.location_id`}
                    options={warehouseList}
                    disabled={checkDisableInputs({ ...item })}
                    handleSearch={({ ...args }) => {
                      handleCustomSearch({
                        ...args,
                        model: "stock.location",

                        args: [
                          ["usage", "in", ["internal", "transit"]],
                          ["company_id", "=", company_id],
                        ],
                      });
                    }}
                  />
                </td>
                <td>
                  <FormikInput
                    name={`inventory.${index}.inventory_quantity`}
                    disabled={checkDisableInputs({
                      isEditable: item.isEditable,
                    })}
                    type="number"
                  />
                </td>
                <td className="d-flex  justify-content-center align-items-center mt-auto">
                  <SVGButton
                    onClick={() => {
                      // console.log("first", item);
                      if (item.isEditable) {
                        handleReadWriteData(item, index);
                        return;
                      }
                      handleToggleInventoryRow(item, index);
                    }}
                    style={{
                      color: isVTEX ? primaryColor.primary : "#ff5837",
                    }}
                    hoverColor={isVTEX ? primaryColor.primary : "#ff5837"}
                    icon={item.isEditable ? "doneIcon" : "editIcon"}
                  />
                  {!item.isDisabled && (
                    <SVGButton
                      onClick={() => {
                        const newInventory = inventory.filter(
                          (_, i) => i !== index
                        );
                        setFieldValue("inventory", newInventory);
                      }}
                      icon={"deleteIcon"}
                      style={{
                        color: isVTEX ? primaryColor.primary : "#ff5837",
                      }}
                      hoverColor={isVTEX ? primaryColor.primary : "#ff5837"}
                    />
                  )}
                </td>
              </tr>
            ))}
            {inventory.length === 0 && (
              <tr>
                <td colSpan={5} className="text-center">
                  No Data Available
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <Button
          onClick={handleAddInventory}
          role="button"
          type="button"
          style={{ color: isVTEX ? "#BAF76A" : "#ff5837" }}
        >
          + {t["Add Inventory"]}
        </Button>
      </div>
    </div>
  );
}

export default ProductInventory;
